const _ = require('./_');

function flatten(entity, convertArrayToIndexFields = false) {
  if (_.isArray(entity)) {
    if (convertArrayToIndexFields) {
      const flattenedArray = { length: entity.length };
      _.each(entity, (item, index) => {
        const flattenedItem = flatten(item, convertArrayToIndexFields);
        if (!_.isDate(flattenedItem) && _.isObject(flattenedItem)) {
          _.each(_.keys(flattenedItem), (itemChildKey) => {
            flattenedArray[`${index}.${itemChildKey}`] = flattenedItem[itemChildKey];
          });
        } else {
          flattenedArray[`${index}`] = flattenedItem;
        }
      });
      return flattenedArray;
    }
  } else if (_.isDate(entity)) {
    return entity;
  } else if (_.isObject(entity)) {
    const flattenedObject = {};
    _.each(_.keys(entity), (k) => {
      const flattenedItem = flatten(entity[k], convertArrayToIndexFields);
      if (!_.isArray(flattenedItem) && !_.isDate(flattenedItem) && _.isObject(flattenedItem)) {
        _.each(_.keys(flattenedItem), (itemChildKey) => {
          flattenedObject[`${k}.${itemChildKey}`] = flattenedItem[itemChildKey];
        });
      } else {
        flattenedObject[k] = flattenedItem;
      }
    });
    return flattenedObject;
  }

  return entity;
}

function unflattenEntity(flattenedEntity) {
  const regularEntity = {};
  _.each(_.keys(flattenedEntity), (k) => {
    const delimiterPos = k.indexOf('.');
    if (delimiterPos > 0) {
      const outerFieldName = k.substr(0, delimiterPos);
      const innerFieldName = k.substr(delimiterPos + 1);
      const childEntity = {};
      childEntity[innerFieldName] = flattenedEntity[k];
      regularEntity[outerFieldName] = unflattenEntity(childEntity);
    } else {
      regularEntity[k] = flattenedEntity[k];
    }
  });

  return regularEntity;
}

function getDelimiters(delimitedFieldName) {
  const regex = /^([^[\]]*)(\[['"]?([^'"\]]*)['"]?\])?/;
  let remainingText = delimitedFieldName;
  let lastRemainingText = remainingText;
  let allTokens = [];
  while (remainingText.length > 0) {
    const matchResult = regex.exec(remainingText);
    const [overallMatch, leadingMatch, , squareBracketAccessor] = matchResult;
    const latestTokens = leadingMatch.split('.').concat([squareBracketAccessor]);
    allTokens = allTokens.concat(_.compact(latestTokens));

    lastRemainingText = remainingText;
    remainingText = remainingText.substring(overallMatch.length);
    if (lastRemainingText === remainingText) {
      throw new Error(`An error occurred parsing "${delimitedFieldName}" (likely mismatched square brackets)`);
    }
  }
  return allTokens;
}

function getFieldValue(theObject, delimitedFieldName, useArrayIndexFields = false) {
  if (!theObject) {
    return undefined;
  }

  let parsingObject = theObject;
  if (theObject.toJSON) {
    // FIXME: objects straight from mongo cause problems when we pass them around with mongo document features
    parsingObject = theObject.toJSON();
  }

  const tokens = getDelimiters(delimitedFieldName);
  const fieldValue = _.reduce(tokens,
    (memo, t) => {
      if (memo) {
        if (Object.prototype.hasOwnProperty.call(memo, t)) {
          return memo[t];
        }
        if (Array.isArray(memo)) {
          const isArrayIndexField = (t === 'length') || _.isNumber(t) || _.isNumber(Number(t));
          if (useArrayIndexFields && isArrayIndexField) {
            if (t === 'length') {
              return memo.length;
            }
            const indexToGet = _.isNumber(t) ? t : Number(t);
            return memo[indexToGet];
          }
          return _.flatten(_.pluck(memo, t));
        }
      }

      return undefined;
    },
    parsingObject);

  return fieldValue;
}

function setFieldValue(theObject, delimitedFieldName, value, useArrayIndexFields = false) {
  const tokens = delimitedFieldName.split('.');
  const parentTokens = tokens.slice(0, tokens.length - 1);
  const childFieldName = tokens[tokens.length - 1];

  let parentFieldValue = theObject;
  _.each(parentTokens, (t) => {
    if (parentFieldValue) {
      if (Object.prototype.hasOwnProperty.call(parentFieldValue, t)) {
        if (!parentFieldValue[t] || typeof parentFieldValue[t] !== 'object') {
          parentFieldValue[t] = {};
        }
        parentFieldValue = parentFieldValue[t];
      } else if (Array.isArray(parentFieldValue)) {
        parentFieldValue = _.flatten(_.pluck(parentFieldValue, t));
      } else {
        // doesn't have the field, create it
        parentFieldValue[t] = {};
        parentFieldValue = parentFieldValue[t];
      }
    }
  });

  if (parentFieldValue === undefined) {
    throw new Error(`Attempted to set ${childFieldName} on an undefined object`);
  } else if (Array.isArray(parentFieldValue)) {
    if (_.isFunction(value)) {
      if (childFieldName) {
        _.each(parentFieldValue, (p) => { p[childFieldName] = value(p[childFieldName]); }); // eslint-disable-line no-param-reassign
      } else {
        for (let i = 0; i < parentFieldValue.length; i += 1) {
          parentFieldValue[i] = value(parentFieldValue[i]);
        }
      }
    } else {
      if (childFieldName) { // eslint-disable-line no-lonely-if
        const isArrayIndexField = (childFieldName === 'length') || _.isNumber(childFieldName) || _.isNumber(Number(childFieldName));
        if (useArrayIndexFields && isArrayIndexField) {
          if (childFieldName === 'length') {
            const newLength = _.isNumber(value) ? value : Number(value);
            parentFieldValue.splice(newLength);
          } else {
            const indexToEdit = _.isNumber(childFieldName) ? childFieldName : Number(childFieldName);
            parentFieldValue[indexToEdit] = value;
          }
        } else {
          _.each(parentFieldValue, (p) => { p[childFieldName] = value; }); // eslint-disable-line no-param-reassign
        }
      } else {
        for (let i = 0; i < parentFieldValue.length; i += 1) {
          parentFieldValue[i] = value;
        }
      }
    }
  } else {
    parentFieldValue[childFieldName] = _.isFunction(value) ? value(parentFieldValue[childFieldName]) : value;
  }

  return theObject;
}

function setFieldValues(theObject, newFieldValues) {
  _.each(_.keys(newFieldValues), (k) => {
    setFieldValue(theObject, k, newFieldValues[k]);
  });

  return theObject;
}

function pickOne(sourceObject, incomingPickedObject, delimitedFieldName) {
  if (_.isArray(sourceObject)) {
    return _.map(sourceObject, (o, i) => {
      let existingArrayValue;
      if (incomingPickedObject && (incomingPickedObject.length > i)) {
        existingArrayValue = incomingPickedObject[i];
      }
      return pickOne(o, existingArrayValue, delimitedFieldName);
    });
  }

  if (_.isObject(sourceObject)) {
    const tokens = delimitedFieldName.split('.');
    const token = tokens[0];
    const remainingFieldName = _.rest(tokens, 1).join('.');

    if (!_.has(sourceObject, token)) {
      return incomingPickedObject;
    }

    const pickedObject = _.extend({}, incomingPickedObject);

    if (_.isArray(sourceObject[token])) {
      pickedObject[token] = _.map(sourceObject[token], (o, i) => {
        let existingArrayValue;
        if (pickedObject[token] && (pickedObject[token].length > i)) {
          existingArrayValue = pickedObject[token][i];
        }
        return pickOne(o, existingArrayValue, remainingFieldName);
      });
    } else if (_.isDate(sourceObject[token])) {
      // dates are objects, so we need to treat them as such
      pickedObject[token] = sourceObject[token];
    } else if (_.isObject(sourceObject[token])) {
      pickedObject[token] = pickOne(sourceObject[token], pickedObject[token], remainingFieldName);
    } else if (tokens.length === 1) {
      pickedObject[token] = sourceObject[token];
    } else {
      throw new Error(`Attempting to retrieve sub-fields from a primitive datatype: ${delimitedFieldName}`);
    }

    return pickedObject;
  }

  // primitive data type (or function, or something that can't go deeper)
  return sourceObject;
}

function pickMany(sourceObject, delimitedFieldNames) {
  const pickedObject = _.reduce(delimitedFieldNames,
    (memo, delimitedFieldName) => pickOne(sourceObject, memo, delimitedFieldName),
    {});

  return pickedObject;
}

module.exports = {
  flatten,
  unflattenEntity,
  getDelimiters,
  getFieldValue,
  setFieldValue,
  setFieldValues,
  pickOne,
  pickMany,
};
